import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { CaseMaterialRow } from './CaseMaterialRow';
import type { GradedSlot, PanelConfig, SlotHold } from '../../types/taActionBoard';
import { HoldOverview } from './HoldOverview';
import { SlotRow } from './SlotRow';
import {
  fullNameFromEmail,
  sortSlotsForDisplay,
  suggestedSlots,
} from './format';

interface SlotListProps {
  panel: PanelConfig | null;
  slots: GradedSlot[];
  slotError: string | null;
  /**
   * Why the window is empty, when the server can say something specific.
   * "Widen the panel or wait for calendars to free up" is the least useful
   * thing to say when a panelist is simply on holiday.
   */
  slotDiagnosis?: string | null;
  /** The window the server graded. Never hardcode it — it has changed once. */
  lookaheadDays?: number;
  /** True while the per-panel re-check is running */
  rechecking?: boolean;
  onEditPanel: () => void;
  /** Role-level action: HOLD events are created for the recruiter's SELECTED slots */
  onBlockSlots: (selected: GradedSlot[]) => void;
  /**
   * Requests-tab embed only (absent on the Scheduling tab): put the ticked slots into the reply
   * editor as concrete time proposals — the mail answers "please propose times" with the times.
   */
  onProposeInReply?: (selected: GradedSlot[]) => void;
  /**
   * Requests-tab embed only: fired with the ticked slots on mount and on EVERY tick change, so
   * the reply text mirrors the selection live (InboundCard.syncProposalSlots decides what text
   * that touches). Separate from onProposeInReply, which is the explicit re-insert.
   */
  onSelectionChange?: (selected: GradedSlot[]) => void;
  /**
   * Requests-tab embed only: the slot starts the reply ALREADY lists (the proxy wrote them from
   * this same grading), ticked on mount instead of the default top three — see defaultSelection.
   */
  initialSelection?: string[];
  /**
   * Requests-tab embed only. False when the proxy has already DECIDED about this reply (it wrote
   * the times, or explained why it kept the promise): the mount-time mirror must not overwrite that
   * decision with whatever this render's grading happens to tick. Ticks by the recruiter still
   * mirror. Default true — the original "ticks are the proposal" behaviour.
   */
  syncOnMount?: boolean;
  /** Role-level: the Slack message proposes the panel's slot options, so it lives here */
  onAskPanelists: (selected: GradedSlot[]) => void;
  /** Re-grade ONLY this panel's calendars (after panel edits elsewhere, or to see fresh availability) */
  onRecheck: () => void;
  /** The role's calendar holds — managed here, in the panel section */
  holds: SlotHold[];
  /** Holds from the previous shape, which no per-slot row can address */
  legacyHolds: number;
  /** Unticking a blocked slot. The caller confirms before deleting anything. */
  onReleaseHold: (hold: SlotHold) => void;
  onReleaseLegacyHolds: () => void;
}

/**
 * Per-role slot area: panel line + the panel-level actions ("Hold selected
 * slots", "Ask panel", "Re-check", "Edit panel"), then the SUGGESTED slots —
 * free first (green dot), needs-move after (orange dot, chronological within
 * each group), fallback options after that (blue dot) — each with a checkbox on
 * the right so the recruiter picks which ones to hold. Everything the server
 * graded but did not suggest sits behind "show all slots".
 *
 * The panel-level actions live HERE and not on the candidate cards on purpose:
 * HOLD events and the ask-panelists Slack message are per ROLE (one set of
 * slots for the panel), so triggering them from a candidate card was misleading.
 */
export function SlotList({
  panel,
  slots,
  slotError,
  slotDiagnosis,
  lookaheadDays,
  rechecking,
  onEditPanel,
  onBlockSlots,
  onProposeInReply,
  onSelectionChange,
  initialSelection,
  syncOnMount = true,
  onAskPanelists,
  onRecheck,
  holds,
  legacyHolds,
  onReleaseHold,
  onReleaseLegacyHolds,
}: SlotListProps) {
  const [showAll, setShowAll] = useState(false);

  // Already blocked ⇒ managed in the overview above, not re-blockable here. A
  // second calendar event on the same time would be invisible there and so
  // impossible to release.
  const blockedStarts = useMemo(() => new Set(holds.map((h) => h.start)), [holds]);

  const suggested = useMemo(() => suggestedSlots(slots), [slots]);
  const remaining = useMemo(() => {
    const shown = new Set(suggested.map((s) => s.start));
    return sortSlotsForDisplay(slots.filter((s) => !shown.has(s.start)));
  }, [slots, suggested]);

  // Pre-select the top 3 suggestions — the old button blocked exactly the top 3
  // with no choice at all, so a sensible default beats an empty selection. The
  // recruiter adjusts via the checkboxes.
  //
  // A Requests-tab embed whose draft ALREADY lists times (the proxy wrote them) pre-ticks exactly
  // those — the ones still in THIS grading, and nothing else. A slot the board no longer offers
  // simply shows unticked, which is the honest picture; falling back to the board's own top three
  // would tick times the reply does not say. Deliberately NOT keyed on `initialSelection`'s array
  // identity: a fresh array per render must not reset the recruiter's ticks.
  const seededKey = initialSelection === undefined ? null : initialSelection.join('|');
  const defaultSelection = useMemo(
    () => {
      if (seededKey !== null) {
        const known = new Set(slots.map((s) => s.start));
        return new Set(
          (seededKey ? seededKey.split('|') : []).filter((start) => known.has(start) && !blockedStarts.has(start))
        );
      }
      return new Set(suggested.filter((s) => !blockedStarts.has(s.start)).slice(0, 3).map((s) => s.start));
    },
    [suggested, blockedStarts, slots, seededKey]
  );
  const [selected, setSelected] = useState<Set<string>>(defaultSelection);
  // A grouped free window offers several possible starts; the recruiter picks
  // one and THAT is what gets blocked. Keyed by the window's own start.
  const [chosenStarts, setChosenStarts] = useState<Record<string, string>>({});
  // Fresh grading (re-check, panel edit, board refresh) → reset to the new
  // default, because a stale selection could point at slots that no longer
  // exist. Adjusted DURING render (the sanctioned reset-on-prop-change pattern)
  // rather than in an effect, which would render one frame with stale state.
  const [lastDefault, setLastDefault] = useState(defaultSelection);
  if (lastDefault !== defaultSelection) {
    setLastDefault(defaultSelection);
    setSelected(defaultSelection);
    // Chosen window starts MUST reset too: a re-check can shrink a window, and
    // a start kept from the old grading would block a time that is no longer
    // free (the key still matches, so nothing else would catch it).
    setChosenStarts({});
  }

  // The cap is per ROLE and counts what is already blocked, so the HOLD button
  // can never promise more holds than the server will create.
  //
  // It is a budget for HOLDS ONLY, and used to gate the checkboxes themselves —
  // which quietly broke the other two actions that read the same ticks. With
  // eight holds outstanding the ninth tick simply did not register, so a slot
  // the recruiter had ticked for "Ask panel" never reached the message and
  // nothing said why (reported Sep 2026). Asking the panel and writing times
  // into a reply create no calendar events, so neither is capped now: the
  // budget is applied where it is spent, on the hold button.
  const MAX_HOLDS = 10;
  const capacity = Math.max(0, MAX_HOLDS - holds.length);
  const startFor = (slot: GradedSlot) => chosenStarts[slot.start] ?? slot.start;
  /** The slot as it would actually be booked (a window collapses to one start). */
  const bookable = (slot: GradedSlot): GradedSlot => {
    const start = startFor(slot);
    if (start === slot.start) return slot;
    const durationMs = Date.parse(slot.end) - Date.parse(slot.start);
    return { ...slot, start, end: new Date(Date.parse(start) + durationMs).toISOString() };
  };

  const toggle = (start: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(start)) next.delete(start);
      else next.add(start);
      return next;
    });

  const pool = [...suggested, ...remaining];
  /**
   * Everything the recruiter ticked — INCLUDING slots that are already held.
   *
   * A held slot used to be filtered out here, which meant the natural order of
   * work (block the promising times, then ask the panel about the ones that
   * need a meeting moved) silently asked about nothing. A hold is our own
   * tentative event and grades as free anyway, so it is no obstacle to asking;
   * it only stops that slot being held a SECOND time, which is the hold
   * button's business and is handled there.
   */
  const selectedSlots = pool.filter((s) => selected.has(s.start)).map(bookable);
  /** The subset the hold button may act on: not already held, and within budget. */
  const holdableSlots = selectedSlots
    .filter((s) => !blockedStarts.has(s.start))
    .slice(0, capacity);
  const holdOverflow = selectedSlots.filter((s) => !blockedStarts.has(s.start)).length - holdableSlots.length;
  // Only needs-move slots are worth asking about: a free slot needs no negotiation.
  const selectedNeedsMove = selectedSlots.filter((s) => s.grade === 'needs-move');

  /**
   * Mirror the selection to the reply editor (Requests-tab embed). Keyed on the CONTENT of the
   * selection rather than the array identity, which is fresh every render — the consumer must
   * hear "the ticks changed", not "the component re-rendered".
   */
  const selectionKey = selectedSlots.map((s) => `${s.start}~${s.end}`).join('|');
  // Keyed on the last selection actually mirrored, not on "has the effect run once": StrictMode
  // runs the mount effect twice in development and the ref survives both runs, so a plain
  // first-run flag would let the probe fire the mirror. With `syncOnMount` off, the mount's own
  // key is recorded and skipped — the proxy's decision stands until the ticks really change.
  const lastSyncedKey = useRef<string | null>(null);
  useEffect(() => {
    if (lastSyncedKey.current === null && !syncOnMount) {
      lastSyncedKey.current = selectionKey;
      return;
    }
    if (lastSyncedKey.current === selectionKey) return;
    lastSyncedKey.current = selectionKey;
    onSelectionChange?.(selectedSlots);
    // Content-keyed on purpose: `selectedSlots` is a fresh array every render, and firing on
    // identity would tell the editor "the ticks changed" on every unrelated re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-gray-600">
          <span className="font-semibold text-gray-800">Panel:</span>{' '}
          {panel?.interviewers?.length
            ? panel.interviewers.map(fullNameFromEmail).join(' + ')
            : 'not configured'}
          {panel?.fallback ? ` · fallback ${fullNameFromEmail(panel.fallback)}` : ''}
          {panel?.minutes ? ` · ${panel.minutes} min` : ''}
          {panel?.note ? <span className="italic text-gray-400"> · {panel.note}</span> : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {onProposeInReply && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onProposeInReply(selectedSlots)}
              disabled={selectedSlots.length === 0}
              title={
                selectedSlots.length === 0
                  ? 'Tick the slots to propose first'
                  : `Put the ${selectedSlots.length} ticked slot${selectedSlots.length === 1 ? '' : 's'} into the reply as concrete time proposals`
              }
            >
              Propose in reply{selectedSlots.length > 0 ? ` (${selectedSlots.length})` : ''}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onBlockSlots(holdableSlots)}
            disabled={holdableSlots.length === 0}
            title={
              capacity === 0
                ? `This role already holds ${MAX_HOLDS} slots — release one above first`
                : holdableSlots.length === 0
                  ? selectedSlots.length > 0
                    ? 'Every ticked slot is already held — see the list above'
                    : 'Tick the slots to hold first'
                  : `Create tentative hold events for ${holdableSlots.length} slot${holdableSlots.length === 1 ? '' : 's'}` +
                    (holdOverflow > 0
                      ? ` — ${holdOverflow} more ticked slot${holdOverflow === 1 ? '' : 's'} will NOT be held (${MAX_HOLDS} holds max per role)`
                      : '')
            }
          >
            Hold selected slots{holdableSlots.length > 0 ? ` (${holdableSlots.length})` : ''}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onAskPanelists(selectedSlots)}
            disabled={!panel?.interviewers?.length || selectedNeedsMove.length === 0}
            title={
              selectedNeedsMove.length === 0
                ? 'Tick a slot that needs a meeting moved — a free slot just needs a hold'
                : `Ask the panel about ${selectedNeedsMove.length} slot${selectedNeedsMove.length === 1 ? '' : 's'} that need a meeting moved`
            }
          >
            Ask panel
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onRecheck}
            disabled={rechecking || !panel?.interviewers?.length}
            title="Refresh ONLY this panel's calendars — much faster than a full board refresh"
          >
            {rechecking ? 'Refreshing…' : 'Refresh'}
          </Button>
          <Button size="sm" variant="ghost" onClick={onEditPanel}>
            Edit panel
          </Button>
        </div>
      </div>

      {panel?.interviewers?.length ? (
        <>
          <CaseMaterialRow material={panel.caseMaterial ?? null} />
          {/* Blockers are managed HERE, with the panel — a hold is a property of
              the panel's calendars, not of one candidate's card. */}
          <HoldOverview
            holds={holds}
            legacyCount={legacyHolds}
            onRelease={onReleaseHold}
            onReleaseLegacy={onReleaseLegacyHolds}
          />
        </>
      ) : null}

      {slotError ? (
        <p className="mt-2 text-xs text-gray-500">
          No times available: {slotError} — nothing can be proposed until the calendars are
          readable. Check the panel members, then re-check.
        </p>
      ) : pool.length === 0 ? (
        <p className="mt-2 text-xs text-gray-500">
          No workable slot in the next {lookaheadDays ?? 10} days
          {slotDiagnosis ? (
            // The specific reason beats the generic advice — being told to widen
            // a panel is useless when the answer is "Anna is on holiday".
            <> — {slotDiagnosis}</>
          ) : (
            <> — widen the panel (edit it) or wait for calendars to free up.</>
          )}{' '}
          "Ask panel" only applies to slots that need a meeting moved.
        </p>
      ) : (
        <>
          <ul className="mt-2 max-h-72 space-y-1 overflow-y-auto pr-1">
            {suggested.map((slot) => (
              <SlotRow
                key={slot.start}
                slot={slot}
                panel={panel}
                checked={selected.has(slot.start)}
                blocked={blockedStarts.has(slot.start)}
                chosenStart={startFor(slot)}
                onChooseStart={(start) => setChosenStarts((prev) => ({ ...prev, [slot.start]: start }))}
                onToggle={() => toggle(slot.start)}
              />
            ))}
          </ul>
          {remaining.length > 0 && (
            <div className="mt-2">
              <button
                onClick={() => setShowAll((v) => !v)}
                className="text-xs font-semibold text-gray-500 hover:text-gray-700"
              >
                {showAll ? '▾' : '▸'} Show all slots ({remaining.length} more, incl. lunch overlaps
                and multi-meeting moves)
              </button>
              {showAll && (
                <ul className="mt-1 max-h-56 space-y-1 overflow-y-auto pr-1">
                  {remaining.map((slot) => (
                    <SlotRow
                      key={`all-${slot.start}`}
                      slot={slot}
                      panel={panel}
                      checked={selected.has(slot.start)}
                      blocked={blockedStarts.has(slot.start)}
                      chosenStart={startFor(slot)}
                      onChooseStart={(start) => setChosenStarts((prev) => ({ ...prev, [slot.start]: start }))}
                      onToggle={() => toggle(slot.start)}
                    />
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
